import { Button } from "../../components/ui/button";
import { SelectTheme } from "../../components/SelectTheme";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from "../../components/ui/input-otp";
import { useEffect, useState } from "react";
import axios from "axios";
import { BaseUrlApi, ErrorMessage } from "../../lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import Cookie from "js-cookie";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";

export default function CheckCodePage() {
  const navigate = useNavigate();
  const [errors, setErrors] = useState([]);
  const email = new URLSearchParams(window.location.search).get("email");

  const [loadingCheckCode, setLoadingCheckCode] = useState(false);
  const handleSubmitCode = async (e) => {
    e.preventDefault();

    try {
      setLoadingCheckCode(true);
      setErrors([]);
      const { data } = await axios.post(`${BaseUrlApi}/check-code`, {
        email,
        reset_code: resetCode,
      });
      toast("تم التحقق من الرمز", {
        description: "تم التحقق من الرمز بنجاح.",
      });
      Cookie.set("token", data.token);
      navigate(`/reset-password`);
    } catch (error) {
      setErrors(ErrorMessage(error));
      Cookie.remove("token");
    } finally {
      setLoadingCheckCode(false);
    }
  };

  const [resetCode, setResetCode] = useState("");
  const [loadingResendCode, setLoadingResendCode] = useState(false);

  const handleResendCode = async (e) => {
    e.preventDefault();
    if (timeLeft === 0) {
      try {
        setLoadingResendCode(true);
        setErrors([]);
        await axios.post(`${BaseUrlApi}/resend-code`, {
          email,
        });
        toast("تم إعادة إرسال الرمز", {
          description: "تم إرسال رمز تحقق جديد إلى بريدك الإلكتروني.",
        });
      } catch (error) {
        setErrors(ErrorMessage(error));
      } finally {
        setLoadingResendCode(false);
      }
    }
  };

  // Count Down 5 min = 300 sec
  const [timeLeft, setTimeLeft] = useState(300);
  useEffect(() => {
    // If timeLeft reaches 0, stop the timer
    if (timeLeft === 0) return;

    // Set an interval that decrements the time every second
    const timerInterval = setInterval(() => {
      setTimeLeft((prevTime) => prevTime - 1);
    }, 1000);

    // Clear the interval when the component is unmounted or when timeLeft reaches 0
    return () => clearInterval(timerInterval);
  }, [timeLeft]);

  // Convert timeLeft from seconds into minutes and seconds
  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;

  return (
    <div className="flex min-h-screen bg-background" dir="rtl">
      {/* Left Section */}
      <div className="relative hidden w-1/2 p-8 lg:block">
        <div className="h-full w-full overflow-hidden rounded-[40px] bg-gradient-to-b from-secondaryColor via-mainColor to-background text-white">
          <div className="flex h-full flex-col items-center justify-center px-8 text-center">
            <div className="mb-8">
              <h1 className="text-2xl font-semibold">فريق أساكيب</h1>
            </div>
            <h2 className="mb-6 text-4xl font-bold">تحقق من هويتك</h2>
            <p className="mb-12 text-lg">
              أدخل رمز التحقق المرسل إلى بريدك الإلكتروني.
            </p>

            <div className="w-full max-w-sm space-y-4">
              <div className="rounded-lg bg-primary-foreground/10 p-4 backdrop-blur-sm">
                <div className="flex items-center gap-4">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-black">
                    ✓
                  </span>
                  <span className="text-lg">أدخل بريدك الإلكتروني</span>
                </div>
              </div>
              <div className="rounded-lg bg-primary-foreground/10 p-4 backdrop-blur-sm">
                <div className="flex items-center gap-4">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-black">
                    ٢
                  </span>
                  <span className="text-lg">التحقق بواسطة الرمز</span>
                </div>
              </div>
              <div className="rounded-lg bg-primary-foreground/5 p-4 backdrop-blur-sm">
                <div className="flex items-center gap-4">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-foreground/20">
                    ٣
                  </span>
                  <span className="text-lg">إنشاء كلمة مرور جديدة</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Section */}
      <div className="flex w-full items-center justify-center bg-background p-6 lg:w-1/2">
        <div className="w-full max-w-md rounded-[40px]">
          <div className="mx-auto max-w-sm">
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-3xl font-bold text-foreground">رمز التحقق</h2>
              <SelectTheme />
            </div>
            <p className="mb-8 text-muted-foreground">
              لقد أرسلنا رمزًا مكونًا من 6 أرقام إلى بريدك الإلكتروني.
            </p>

            <form className="space-y-4" onSubmit={handleSubmitCode}>
              <div className="grid justify-center gap-2">
                <InputOTP
                  maxLength={6}
                  value={resetCode}
                  onChange={(code) => setResetCode(code)}
                >
                  <InputOTPGroup>
                    <InputOTPSlot className="h-12 w-12" index={0} />
                    <InputOTPSlot className="h-12 w-12" index={1} />
                  </InputOTPGroup>
                  <InputOTPSeparator />
                  <InputOTPGroup>
                    <InputOTPSlot className="h-12 w-12" index={2} />
                    <InputOTPSlot className="h-12 w-12" index={3} />
                  </InputOTPGroup>
                  <InputOTPSeparator />
                  <InputOTPGroup>
                    <InputOTPSlot className="h-12 w-12" index={4} />
                    <InputOTPSlot className="h-12 w-12" index={5} />
                  </InputOTPGroup>
                </InputOTP>

                {errors?.reset_code || errors ? (
                  <p className="text-red-500 font-semibold text-sm text-center">
                    {Array.isArray(errors?.reset_code)
                      ? errors?.reset_code[0]
                      : errors}
                  </p>
                ) : null}
              </div>

              <div className="text-center">
                <p className="text-sm text-muted-foreground">لم تستلم الرمز؟</p>
                <Button
                  variant={"link"}
                  className="p-0"
                  disabled={timeLeft !== 0 || loadingResendCode}
                  onClick={handleResendCode}
                  type="button"
                >
                  <p>إعادة إرسال الرمز</p>
                  {timeLeft !== 0 && (
                    <span className="animate-pulse">
                      {minutes < 10 ? "0" + minutes : minutes}:
                      {seconds < 10 ? "0" + seconds : seconds}
                    </span>
                  )}
                </Button>
              </div>

              <Button
                className="h-12 w-full"
                type="submit"
                disabled={loadingCheckCode}
              >
                {loadingCheckCode && (
                  <div className="animate-spin mb-px">
                    <Loader2 />
                  </div>
                )}
                التحقق من الرمز
              </Button>

              <p className="text-center text-sm text-muted-foreground">
                <Link
                  to="/forgot-password"
                  className="text-primary hover:underline"
                >
                  رجوع
                </Link>
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
